import { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import {
  Search, RefreshCw, AlertCircle, CheckCircle,
  Users as UsersIcon, Trash2, Eye, UserCheck, UserX, Mail, Phone, Calendar, Building
} from 'lucide-react'
import { userService, User, UserStats, UserDeletionImpactData, BookingDeletionBreakdown, formatUserRole, formatVerificationStatus, formatRelativeTime } from '../services/userService'
import { useAuth } from '../contexts/AuthContext'
import ConfirmationDialog from '../components/ConfirmationDialog'
import AdminPagination from '../components/AdminPagination'

const EMPTY_BOOKING_BREAKDOWN: BookingDeletionBreakdown = {
  total: 0,
  pending: 0,
  approved: 0,
  cancelled: 0,
  rejected: 0,
  activeUpcomingProgramMonths: 0,
  activeCurrentProgramMonth: 0,
  activePastProgramMonths: 0,
  activeUnknownProgramMonth: 0,
  activeTotal: 0,
}

function formatSchoolBookingImpactLines(b: BookingDeletionBreakdown): string[] {
  const lines: string[] = []
  if (b.total === 0) {
    lines.push('  (No bookings on those schools.)')
    return lines
  }
  lines.push(
    `  Status: ${b.pending} pending, ${b.approved} approved, ${b.cancelled} cancelled, ${b.rejected} rejected.`,
  )
  if (b.activeTotal > 0) {
    lines.push(
      `  Active (pending or approved) by program month on the booking: ${b.activeUpcomingProgramMonths} upcoming (future months), ${b.activeCurrentProgramMonth} this month (in progress), ${b.activePastProgramMonths} past months (still open), ${b.activeUnknownProgramMonth} unknown or non-standard month.`,
    )
    lines.push(
      '  WARNING: Deleting removes these school bookings and any linked payment orders. Customers lose those records in your system.',
    )
  }
  return lines
}

function formatCustomerBookingImpactLines(b: BookingDeletionBreakdown): string[] {
  const lines: string[] = []
  if (b.total === 0) {
    lines.push('• No bookings where this user is the customer (separate from bookings at schools they own).')
    return lines
  }
  lines.push(`• Bookings where this user is the customer (${b.total} total, any school):`)
  lines.push(
    `  Status: ${b.pending} pending, ${b.approved} approved, ${b.cancelled} cancelled, ${b.rejected} rejected.`,
  )
  if (b.activeTotal > 0) {
    lines.push(
      `  Active by program month: ${b.activeUpcomingProgramMonths} upcoming, ${b.activeCurrentProgramMonth} this month, ${b.activePastProgramMonths} past months still open, ${b.activeUnknownProgramMonth} unknown month.`,
    )
  }
  lines.push('  (A yoga school account can still have personal bookings elsewhere; those are listed here.)')
  return lines
}

function buildPermanentDeleteDetails(
  impact: UserDeletionImpactData | null,
  loading: boolean,
  err: string | null,
): string {
  if (err) {
    return `Could not load deletion summary: ${err}\n\nSuper Admins can still proceed; the server will remove owned schools (courses, bookings, orders, messages, rooms), personal bookings, reviews, messages, consultations, and testimonials.`
  }
  if (loading || !impact) {
    return 'Loading what will be removed…'
  }
  const lines: string[] = []
  lines.push('This permanently deletes the account and related data:\n')
  if (impact.lastSuperAdminWarning) {
    lines.push('• Deletion is blocked: this is the only Super Admin account.')
    return lines.join('\n')
  }
  if (impact.staffMembershipsRemoved > 0) {
    lines.push(
      `• Remove this user from staff on ${impact.staffMembershipsRemoved} other school(s) (those schools are not deleted).`,
    )
  }
  if (impact.schoolsDeleted > 0) {
    lines.push(
      `• Delete ${impact.schoolsDeleted} yoga school listing(s) this user owns, including all courses, bookings, messages, and rooms for those schools.`,
    )
    if (impact.schoolNames.length > 0) {
      const extra = impact.schoolNames.length < impact.schoolsDeleted ? ' …' : ''
      lines.push(`  Names: ${impact.schoolNames.join(', ')}${extra}`)
    }
    const sb = impact.ownedSchoolBookingsBreakdown ?? EMPTY_BOOKING_BREAKDOWN
    lines.push(`  ${impact.coursesOnOwnedSchools} course(s) on those schools; ${sb.total} booking(s) on those schools:`)
    lines.push(...formatSchoolBookingImpactLines(sb))
  }
  lines.push(...formatCustomerBookingImpactLines(impact.customerBookingsBreakdown ?? EMPTY_BOOKING_BREAKDOWN))
  lines.push(`• ${impact.courseReviews} course review(s).`)
  lines.push(`• ${impact.messages} message(s) (sent or received).`)
  lines.push(`• ${impact.consultations} consultation(s).`)
  lines.push(`• ${impact.testimonials} testimonial(s).`)
  if (impact.usersWithCreatedByAdminRef > 0 || impact.coursesWithCreatedByAdminRef > 0) {
    lines.push(
      `• Clear "created by admin" on ${impact.usersWithCreatedByAdminRef} user(s) and ${impact.coursesWithCreatedByAdminRef} course(s) if this user onboarded them.`,
    )
  }
  lines.push('\nType DELETE (all caps) below to confirm.')
  return lines.join('\n')
}

const Users = () => {
  const { user: adminUser } = useAuth()
  const [users, setUsers] = useState<User[]>([])
  const [stats, setStats] = useState<UserStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('ALL')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [userToDelete, setUserToDelete] = useState<User | null>(null)
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('')
  const [deletionImpact, setDeletionImpact] = useState<UserDeletionImpactData | null>(null)
  const [deletionImpactLoading, setDeletionImpactLoading] = useState(false)
  const [deletionImpactError, setDeletionImpactError] = useState<string | null>(null)
  const [showToggleModal, setShowToggleModal] = useState(false)
  const [userToToggle, setUserToToggle] = useState<User | null>(null)
  const [verificationUpdatingId, setVerificationUpdatingId] = useState<string | null>(null)

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await userService.getUsers({
        page: currentPage,
        limit: 10,
        role: roleFilter !== 'ALL' ? roleFilter : undefined,
        isActive: statusFilter !== 'ALL' ? statusFilter : undefined,
        search: searchTerm || undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      })
      if (response.success && response.data) {
        setUsers(response.data.users)
        const p = response.data.pagination
        setTotalPages(Math.max(1, p.totalPages || 1))
        setTotalItems(typeof p.totalItems === 'number' ? p.totalItems : 0)
      }
    } catch (error: any) {
      setError(error.message || 'Failed to fetch users')
    } finally {
      setLoading(false)
    }
  }, [currentPage, roleFilter, statusFilter, searchTerm])

  const fetchStats = useCallback(async () => {
    try {
      const response = await userService.getUserStats()
      if (response.success && response.data) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }, [])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    fetchStats()
  }, [])

  useEffect(() => {
    if (!showDeleteModal || !userToDelete || adminUser?.role !== 'SUPER_ADMIN') {
      return
    }
    let cancelled = false
    setDeletionImpact(null)
    setDeletionImpactError(null)
    setDeletionImpactLoading(true)
    userService
      .getUserDeletionImpact(userToDelete._id)
      .then((res) => {
        if (!cancelled && res.success && res.data) {
          setDeletionImpact(res.data)
        }
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setDeletionImpactError(e.message || 'Failed to load deletion summary')
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDeletionImpactLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [showDeleteModal, userToDelete?._id, adminUser?.role])

  const handleUserAction = (action: string, user: User) => {
    switch (action) {
      case 'view':
        setSelectedUser(user)
        setShowModal(true)
        break
      case 'toggle':
        setUserToToggle(user)
        setShowToggleModal(true)
        break
      case 'delete':
        setDeleteConfirmInput('')
        setDeletionImpact(null)
        setDeletionImpactError(null)
        setUserToDelete(user)
        setShowDeleteModal(true)
        break
    }
  }

  const handleToggleConfirm = async () => {
    if (!userToToggle) return
    
    try {
      setActionLoading(userToToggle._id)
      await userService.toggleUserStatus(userToToggle._id, !userToToggle.isActive)
      await fetchUsers()
      await fetchStats()
      setShowToggleModal(false)
      setUserToToggle(null)
    } catch (error: any) {
      setError(error.message)
    } finally {
      setActionLoading(null)
    }
  }

  const handleApproveVerification = async () => {
    if (!selectedUser || selectedUser.role !== 'YOGA_SCHOOL') return
    const id = selectedUser._id
    try {
      setVerificationUpdatingId(id)
      setError(null)
      const res = await userService.setYogaSchoolVerificationStatus(id, 'VERIFIED')
      if (res.success && res.data) {
        setSelectedUser(res.data)
        await fetchUsers()
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update verification status')
    } finally {
      setVerificationUpdatingId(null)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!userToDelete || adminUser?.role !== 'SUPER_ADMIN') return
    if (deleteConfirmInput !== 'DELETE') {
      toast.error('Type DELETE to confirm')
      return
    }
    if (deletionImpact?.lastSuperAdminWarning) {
      toast.error('Cannot delete the last Super Admin account')
      return
    }

    try {
      setActionLoading(userToDelete._id)
      await userService.permanentlyDeleteUser(userToDelete._id, 'DELETE')
      await fetchUsers()
      await fetchStats()
      toast.success('User permanently deleted')
      setShowDeleteModal(false)
      setUserToDelete(null)
      setDeleteConfirmInput('')
      setDeletionImpact(null)
      setDeletionImpactError(null)
    } catch (error: any) {
      setError(error.message)
      toast.error(error.message || 'Deletion failed')
    } finally {
      setActionLoading(null)
    }
  }

  const getRoleBadge = (role: User['role']) => {
    const colors = {
      CUSTOMER: 'bg-blue-100 text-blue-800',
      YOGA_SCHOOL: 'bg-green-100 text-green-800',
      SUPPORT_ADMIN: 'bg-yellow-100 text-yellow-800',
      SUPER_ADMIN: 'bg-red-100 text-red-800'
    }
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[role]}`}>
        {formatUserRole(role)}
      </span>
    )
  }

  const getStatusBadge = (user: User) => {
    if (user.role === 'YOGA_SCHOOL') {
      const docStatus = user.verificationStatus ?? 'PENDING'
      const colors = {
        PENDING: 'bg-yellow-100 text-yellow-800',
        VERIFIED: 'bg-green-100 text-green-800',
        REJECTED: 'bg-red-100 text-red-800'
      }
      return (
        <div className="flex flex-col gap-2">
          <span className={`inline-flex w-fit px-2 py-1 rounded-full text-xs font-medium ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {`Account: ${user.isActive ? 'Active' : 'Inactive'}`}
          </span>
          <span className={`inline-flex w-fit px-2 py-1 rounded-full text-xs font-medium ${colors[docStatus]}`}>
            {`Verification: ${formatVerificationStatus(docStatus)}`}
          </span>
        </div>
      )
    }
    return (
      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {`Account: ${user.isActive ? 'Active' : 'Inactive'}`}
      </span>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Users</h1>
          <p className="text-gray-600">Manage all platform users and their permissions</p>
        </div>
        <div className="text-sm text-gray-500">
          {totalItems.toLocaleString()} matching users
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Users', value: stats.totalUsers, color: 'bg-blue-500', icon: UsersIcon },
            { label: 'Active Users', value: stats.activeUsers, color: 'bg-green-500', icon: UserCheck },
            { label: 'Yoga School Users', value: stats.yogaSchools, color: 'bg-purple-500', icon: Building },
            { label: 'Verified', value: stats.verifiedUsers, color: 'bg-yellow-500', icon: CheckCircle }
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">{stat.label}</p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`p-4 rounded-2xl ${stat.color} shadow-lg`}>
                  <stat.icon className="h-8 w-8 text-white" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setCurrentPage(1)
              }}
              className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value)
              setCurrentPage(1)
            }}
            className="w-full sm:w-48 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
          >
            <option value="ALL">All Roles</option>
            <option value="CUSTOMER">Customers</option>
            <option value="YOGA_SCHOOL">Yoga Schools</option>
            <option value="SUPPORT_ADMIN">Support Admins</option>
            <option value="SUPER_ADMIN">Super Admins</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setCurrentPage(1)
            }}
            className="w-full sm:w-48 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
          >
            <option value="ALL">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="px-4 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800 font-medium">{error}</span>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">User</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Role</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Statuses</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Contact</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Last Login</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <RefreshCw className="w-5 h-5 animate-spin text-amber-500" />
                      <span className="text-gray-500">Loading users...</span>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user, index) => (
                  <motion.tr
                    key={user._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-amber-600">
                            {`${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}` ||
                              (user.email?.[0]?.toUpperCase() ?? '?')}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {[user.firstName, user.lastName].filter(Boolean).join(' ') ||
                              user.email}
                          </p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                          {user.businessName && (
                            <p className="text-sm text-gray-600 flex items-center">
                              <Building className="w-3 h-3 mr-1" />
                              {user.businessName}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">{getRoleBadge(user.role)}</td>
                    <td className="py-4 px-6">{getStatusBadge(user)}</td>
                    <td className="py-4 px-6">
                      <div className="space-y-1">
                        <div className="flex items-center text-sm text-gray-600">
                          <Mail className="w-3 h-3 mr-2" />
                          {user.email}
                        </div>
                        {user.phone && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Phone className="w-3 h-3 mr-2" />
                            {user.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-sm text-gray-600">
                        {user.lastLogin ? (
                          <div className="flex items-center">
                            <Calendar className="w-3 h-3 mr-1" />
                            {formatRelativeTime(user.lastLogin)}
                          </div>
                        ) : (
                          'Never'
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleUserAction('view', user)}
                          className="p-2 text-gray-600 hover:text-amber-600 transition-colors rounded-lg hover:bg-amber-50"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleUserAction('toggle', user)}
                          disabled={actionLoading === user._id}
                          className={`p-2 transition-colors rounded-lg ${
                            user.isActive 
                              ? 'text-gray-600 hover:text-red-600 hover:bg-red-50' 
                              : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                          } disabled:opacity-50`}
                          title={user.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {actionLoading === user._id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : user.isActive ? (
                            <UserX className="w-4 h-4" />
                          ) : (
                            <UserCheck className="w-4 h-4" />
                          )}
                        </button>
                        {adminUser?.role === 'SUPER_ADMIN' && (
                        <button
                          onClick={() => handleUserAction('delete', user)}
                          disabled={actionLoading === user._id}
                          className="p-2 text-gray-600 hover:text-red-600 transition-colors rounded-lg hover:bg-red-50 disabled:opacity-50"
                          title="Permanently delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AdminPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        disabled={loading}
        onPageChange={setCurrentPage}
        className="rounded-2xl shadow-sm"
      />

      {/* User Detail Modal */}
      {showModal && selectedUser && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{ 
            position: 'fixed',
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            width: '100vw', 
            height: '100vh', 
            zIndex: 99998,
            margin: 0,
            padding: '16px',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setShowModal(false)}
        >
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -50, opacity: 0 }}
            className="bg-white rounded-2xl p-8 shadow-xl max-w-2xl w-full space-y-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4">
              User Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-600">Name</p>
                <p className="text-lg font-semibold text-gray-900">
                  {[selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(' ') ||
                    selectedUser.email}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Email</p>
                <p className="text-lg font-semibold text-gray-900">{selectedUser.email}</p>
              </div>
              {selectedUser.phone && (
                <div>
                  <p className="text-sm font-medium text-gray-600">Phone</p>
                  <p className="text-lg font-semibold text-gray-900">{selectedUser.phone}</p>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-gray-600">Role</p>
                <p className="text-lg font-semibold text-gray-900">{formatUserRole(selectedUser.role)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Account Status</p>
                <p className="text-lg font-semibold text-gray-900">
                  {selectedUser.isActive ? 'Active' : 'Inactive'}
                </p>
              </div>
              {selectedUser.role === 'YOGA_SCHOOL' && selectedUser.businessName && (
                <div>
                  <p className="text-sm font-medium text-gray-600">Business Name</p>
                  <p className="text-lg font-semibold text-gray-900">{selectedUser.businessName}</p>
                </div>
              )}
              {selectedUser.role === 'YOGA_SCHOOL' && (
                <div className="md:col-span-2">
                  <p className="text-sm font-medium text-gray-600">Verification Status</p>
                  <div className="mt-1 flex flex-wrap items-center gap-3">
                    <p className="text-lg font-semibold text-gray-900">
                      {formatVerificationStatus(selectedUser.verificationStatus ?? 'PENDING')}
                    </p>
                    {(selectedUser.verificationStatus ?? 'PENDING') !== 'VERIFIED' && (
                      <button
                        type="button"
                        onClick={handleApproveVerification}
                        disabled={verificationUpdatingId === selectedUser._id}
                        className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {verificationUpdatingId === selectedUser._id ? 'Saving…' : 'Mark verification as approved'}
                      </button>
                    )}
                  </div>
   
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-gray-600">Registered On</p>
                <p className="text-lg font-semibold text-gray-900">
                  {new Date(selectedUser.createdAt).toLocaleDateString()}
                </p>
              </div>
              {selectedUser.lastLogin && (
                <div>
                  <p className="text-sm font-medium text-gray-600">Last Login</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatRelativeTime(selectedUser.lastLogin)}
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-3 border-t border-gray-200 pt-6">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Toggle Status Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showToggleModal}
        onClose={() => {
          setShowToggleModal(false)
          setUserToToggle(null)
        }}
        onConfirm={handleToggleConfirm}
        title={userToToggle?.isActive ? 'Deactivate User' : 'Activate User'}
        message={`Are you sure you want to ${userToToggle?.isActive ? 'deactivate' : 'activate'} this user?`}
        details={userToToggle ? `User: ${[userToToggle.firstName, userToToggle.lastName].filter(Boolean).join(' ') || userToToggle.email}\nEmail: ${userToToggle.email}\nRole: ${formatUserRole(userToToggle.role)}\n\n${userToToggle.isActive ? 'This user will be unable to log in and access their account. All their active sessions will be terminated.' : 'This user will be able to log in and access their account normally.'}` : ''}
        confirmText={userToToggle?.isActive ? 'Deactivate' : 'Activate'}
        type={userToToggle?.isActive ? 'warning' : 'success'}
        isLoading={actionLoading === userToToggle?._id}
        icon={userToToggle?.isActive ? <UserX className="w-6 h-6" /> : <UserCheck className="w-6 h-6" />}
      />

      {/* Permanent delete — Super Admin only; requires typing DELETE */}
      <ConfirmationDialog
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setUserToDelete(null)
          setDeleteConfirmInput('')
          setDeletionImpact(null)
          setDeletionImpactError(null)
        }}
        onConfirm={handleDeleteConfirm}
        title="Permanently delete user"
        message="This removes the user account and related records from the database. This cannot be undone."
        details={
          userToDelete
            ? `${[userToDelete.firstName, userToDelete.lastName].filter(Boolean).join(' ') || userToDelete.email}\nEmail: ${userToDelete.email}\nRole: ${formatUserRole(userToDelete.role)}\n\n${buildPermanentDeleteDetails(
                deletionImpact,
                deletionImpactLoading,
                deletionImpactError,
              )}`
            : ''
        }
        confirmText="Permanently delete"
        type="danger"
        isLoading={actionLoading === userToDelete?._id}
        icon={<Trash2 className="w-6 h-6" />}
        confirmDisabled={
          adminUser?.role !== 'SUPER_ADMIN' ||
          deletionImpactLoading ||
          deletionImpact?.lastSuperAdminWarning === true ||
          deleteConfirmInput !== 'DELETE'
        }
        confirmInputLabel="Type DELETE to confirm"
        confirmInputPlaceholder="DELETE"
        confirmInputValue={deleteConfirmInput}
        onConfirmInputChange={setDeleteConfirmInput}
      />

    </div>
  )
}

export default Users
